import React, { useState, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Share2, TrendingUp, TrendingDown, Sparkles, RefreshCw, Edit3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { rassegne, articles, tierLabels, contentTypeLabels, dashboardKPIs, coverageByTier, sentimentOverTime, topSources, spokespersonMentions, executiveSummary, sentimentLabels } from '../lib/data';
function getSentimentColor(sentiment: string) {
  switch (sentiment) {
    case 'positive':
      return 'bg-sentiment-positive';
    case 'neutral':
      return 'bg-sentiment-neutral';
    case 'negative':
      return 'bg-sentiment-negative';
    default:
      return 'bg-muted';
  }
}
function getTierColor(tier: number | string) {
  switch (tier) {
    case 1:
      return 'bg-tier-1 text-white';
    case 2:
      return 'bg-tier-2 text-white';
    case 3:
      return 'bg-tier-3 text-white';
    case 'specialized':
      return 'bg-tier-special text-white';
    default:
      return 'bg-muted';
  }
}
function KPI({
  label,
  value,
  trend,
  accent = false
}: {
  label: string;
  value: string | number;
  trend?: number;
  accent?: boolean;
}) {
  return <div className="flex flex-col gap-2" data-id="element-663">
      <div className="text-sm text-muted-foreground" data-id="element-664">{label}</div>
      <div className={`${`text-3xl md:text-4xl font-semibold leading-none ${accent ? 'text-accent' : 'text-foreground'}`} gap-[0px]`} style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-665">
        {value}
      </div>
      {trend !== undefined && <div className="flex items-center gap-1.5 text-sm bg-[#FFFFFF]" data-id="element-666">
          {trend > 0 ? <TrendingUp className="h-3.5 w-3.5 text-sentiment-positive" data-id="element-667" /> : <TrendingDown className="h-3.5 w-3.5 text-sentiment-negative" data-id="element-668" />}
          <span className={trend > 0 ? 'text-sentiment-positive' : 'text-sentiment-negative'} data-id="element-669">
            {trend > 0 ? '+' : ''}
            {trend}%
          </span>
          <span className="text-muted-foreground" data-id="element-670">vs prec.</span>
        </div>}
    </div>;
}
export function RassegnaDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const rassegna = rassegne.find(r => r.id === id) || rassegne[0];
  const rassegnaArticles = articles;
  const topArticles = articles.filter(a => a.status === 'approved').slice(0, 5);
  return <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col gap-[0px]" data-id="element-671">
      {/* Header + Tabs as one white block */}
      <div className="bg-card border-b border-border" data-id="element-672">
        <div className="px-6 pt-6 md:pt-8 max-w-[1400px] mx-auto w-full" data-id="element-673">
          <button onClick={() => navigate('/rassegne')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors" data-id="element-674">
            <ArrowLeft className="h-3.5 w-3.5" data-id="element-675" />
            Rassegne
          </button>

          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6" data-id="element-676">
            <div className="min-w-0" data-id="element-677">
              <h1 className="text-2xl md:text-3xl font-semibold tracking-tight leading-tight mb-2" data-id="element-678">
                {rassegna.name}
              </h1>
              <div className="text-sm text-muted-foreground" data-id="element-679">
                {rassegna.dateRange}
              </div>
            </div>

            <div className="flex items-center gap-2.5 flex-shrink-0" data-id="element-680">
              <Button variant="outline" className="rounded-full border-accent text-accent hover:bg-accent/10 hover:text-accent h-11 px-6 text-sm" data-id="element-681">
                <Share2 className="h-4 w-4 md:mr-2" data-id="element-682" />
                <span className="hidden md:inline" data-id="element-683">Condividi</span>
              </Button>
              <Button className="rounded-full h-11 px-6 text-sm" data-id="element-684">
                <Download className="h-4 w-4 md:mr-2" data-id="element-685" />
                <span className="hidden md:inline" data-id="element-686">Esporta PDF</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab bar - sticky */}
      <div className="border-b border-border sticky top-0 z-10 bg-card" data-id="element-687">
        <div className="px-6 max-w-[1400px] mx-auto w-full flex items-end gap-0" data-id="element-688">
          {[{
          value: 'overview',
          label: 'Panoramica'
        }, {
          value: 'analysis',
          label: 'Analisi'
        }, {
          value: 'articles',
          label: `Articoli`,
          count: rassegna.articleCount
        }].map(tab => <button key={tab.value} onClick={() => setActiveTab(tab.value)} className="relative px-5 py-3 text-sm transition-colors" style={{
          backgroundColor: activeTab === tab.value ? '#FFFFFF' : 'transparent',
          color: activeTab === tab.value ? 'var(--foreground)' : 'var(--muted-foreground)',
          fontWeight: activeTab === tab.value ? 500 : 300
        }} data-id="element-689">
              {tab.label}
              {tab.count !== undefined && <span className="ml-2 text-xs" style={{
            color: 'var(--muted-foreground)'
          }} data-id="element-690">
                  {tab.count}
                </span>}
              {activeTab === tab.value && <span className="absolute bottom-0 left-0 right-0" style={{
            height: '2px',
            backgroundColor: '#0187c3'
          }} data-id="element-691" />}
            </button>)}
        </div>
      </div>

      {/* Content */}
      <div data-id="element-692">
        <TabsContent value="overview" className="m-0 p-6 max-w-[1400px] mx-auto space-y-8" data-id="element-693">
          {/* KPI strip */}
          <section className="bg-card border border-border rounded-lg p-6 grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10" data-id="element-694">
            <KPI label="Articoli totali" value={dashboardKPIs.totalArticles.value} trend={dashboardKPIs.totalArticles.trend} data-id="element-695" />
            <KPI label="Score medio" value={dashboardKPIs.avgRelevanceScore.value.toFixed(1)} accent data-id="element-696" />
            <KPI label="AVE totale" value={`€${(dashboardKPIs.totalAVE.value / 1000).toFixed(0)}k`} trend={dashboardKPIs.totalAVE.trend} data-id="element-697" />
            <div className="flex flex-col gap-2" data-id="element-698">
              <div className="text-sm text-muted-foreground" data-id="element-699">Sentiment</div>
              <div className="flex items-baseline gap-2 leading-none" data-id="element-700">
                <span className="text-2xl md:text-3xl font-semibold text-sentiment-positive" style={{
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-701">
                  {dashboardKPIs.sentimentMix.positive}
                </span>
                <span className="text-sm text-muted-foreground" data-id="element-702">/</span>
                <span className="text-xl text-muted-foreground" style={{
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-703">
                  {dashboardKPIs.sentimentMix.neutral}
                </span>
                <span className="text-sm text-muted-foreground" data-id="element-704">/</span>
                <span className="text-xl text-sentiment-negative" style={{
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-705">
                  {dashboardKPIs.sentimentMix.negative}
                </span>
              </div>
              <div className="text-sm text-muted-foreground" data-id="element-706">
                Positivi / neutri / negativi
              </div>
            </div>
          </section>

          {/* Executive Summary */}
          <section className="bg-card border border-border rounded-lg p-6" data-id="element-707">
            <div className="border-l-2 border-accent pl-5 md:pl-6" data-id="element-708">
              <div className="flex items-center justify-between mb-3" data-id="element-709">
                <div className="flex items-center gap-2 text-sm text-muted-foreground" data-id="element-710">
                  <Sparkles className="h-3.5 w-3.5 text-accent" data-id="element-711" />
                  <span data-id="element-712">Riepilogo AI</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-713">
                  <Button variant="ghost" size="sm" className="h-9 px-3.5 text-sm text-muted-foreground" data-id="element-714">
                    <RefreshCw className="h-3.5 w-3.5 md:mr-1.5" data-id="element-715" />
                    <span className="hidden md:inline" data-id="element-716">Rigenera</span>
                  </Button>
                  <Button variant="ghost" size="sm" className="h-9 px-3.5 text-sm text-muted-foreground" data-id="element-717">
                    <Edit3 className="h-3.5 w-3.5 md:mr-1.5" data-id="element-718" />
                    <span className="hidden md:inline" data-id="element-719">Modifica</span>
                  </Button>
                </div>
              </div>
              <p className="text-base leading-relaxed text-foreground" data-id="element-720">
                {executiveSummary}
              </p>
            </div>
          </section>

          {/* Top Articles */}
          <section className="bg-card border border-border rounded-lg p-6" data-id="element-721">
            <div className="flex items-baseline justify-between mb-5" data-id="element-722">
              <h2 className="text-lg font-semibold" data-id="element-723">Top articoli</h2>
            </div>
            <div className="space-y-1" data-id="element-724">
              {topArticles.map((article, i) => <Fragment key={article.id} data-id="element-725">
                  {i > 0 && <Separator data-id="element-726" />}
                  <button onClick={() => navigate(`/article/${article.id}`)} className="w-full text-left py-4 md:py-5 group hover:bg-muted/30 -mx-3 px-3 rounded-md transition-colors" data-id="element-727">
                    <div className="flex items-start gap-4" data-id="element-728">
                      <img src={article.sourceLogo} alt={article.source} className="w-9 h-9 rounded flex-shrink-0 mt-0.5" data-id="element-729" />
                      <div className="flex-1 min-w-0" data-id="element-730">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5 text-xs text-muted-foreground" data-id="element-731">
                          <Badge className={`text-[10px] px-1.5 py-0 border-0 ${getTierColor(article.tier)}`} data-id="element-732">
                            {tierLabels[article.tier]}
                          </Badge>
                          <span data-id="element-733">{article.source}</span>
                          <span data-id="element-734">·</span>
                          <span data-id="element-735">
                            {new Date(article.date).toLocaleDateString('it-IT', {
                          day: 'numeric',
                          month: 'short'
                        })}
                          </span>
                        </div>
                        <h3 className="font-medium text-sm md:text-base leading-snug mb-2 line-clamp-2" data-id="element-736">
                          {article.title}
                        </h3>
                        <div className="flex items-center gap-5 text-sm" data-id="element-737">
                          <div className="flex items-baseline gap-1.5" data-id="element-738">
                            <span className="font-semibold text-foreground" style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-739">
                              {article.relevanceScore.toFixed(1)}
                            </span>
                            <span className="text-xs text-muted-foreground" data-id="element-740">
                              score
                            </span>
                          </div>
                          <div className="flex items-baseline gap-1.5 text-muted-foreground" data-id="element-741">
                            <span style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-742">
                              €{(article.ave / 1000).toFixed(1)}k
                            </span>
                            <span className="text-xs" data-id="element-743">AVE</span>
                          </div>
                          <div className={`w-1.5 h-1.5 rounded-full ${getSentimentColor(article.sentiment)}`} title={sentimentLabels[article.sentiment]} data-id="element-744" />
                        </div>
                      </div>
                    </div>
                  </button>
                </Fragment>)}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="analysis" className="m-0 p-6 max-w-[1400px] mx-auto space-y-8" data-id="element-745">
          {/* Sentiment over time */}
          <section className="bg-card border border-border rounded-lg p-6" data-id="element-746">
            <h3 className="text-base font-semibold mb-6" data-id="element-747">
              Sentiment nel tempo
            </h3>
            <div className="h-64" data-id="element-748">
              <ResponsiveContainer width="100%" height="100%" data-id="element-749">
                <AreaChart data={sentimentOverTime} margin={{
                top: 5,
                right: 0,
                bottom: 0,
                left: -20
              }} data-id="element-750">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E5" vertical={false} data-id="element-751" />
                  <XAxis dataKey="date" tick={{
                  fontSize: 12
                }} stroke="#8B8680" axisLine={false} tickLine={false} dy={10} data-id="element-752" />
                  <YAxis tick={{
                  fontSize: 12
                }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-753" />
                  <Area type="monotone" dataKey="positive" stackId="1" stroke="#0187c3" fill="#0187c3" fillOpacity={0.5} strokeWidth={1.5} data-id="element-754" />
                  <Area type="monotone" dataKey="neutral" stackId="1" stroke="#64B5F6" fill="#64B5F6" fillOpacity={0.35} strokeWidth={1.5} data-id="element-755" />
                  <Area type="monotone" dataKey="negative" stackId="1" stroke="#1A3A5C" fill="#1A3A5C" fillOpacity={0.5} strokeWidth={1.5} data-id="element-756" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-757">
            {/* Coverage by Tier */}
            <section className="bg-card border border-border rounded-lg p-6" data-id="element-758">
              <h3 className="text-base font-semibold mb-6" data-id="element-759">
                Copertura per tier
              </h3>
              <div className="space-y-4" data-id="element-760">
                {coverageByTier.map(item => <div key={item.tier} data-id="element-761">
                    <div className="flex items-center justify-between mb-2 text-sm" data-id="element-762">
                      <span className="text-foreground font-medium" data-id="element-763">
                        {item.tier}
                      </span>
                      <span className="text-muted-foreground" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-764">
                        {item.count}
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden" data-id="element-765">
                      <div className="h-full bg-foreground/80 rounded-full" style={{
                    width: `${item.percentage}%`
                  }} data-id="element-766" />
                    </div>
                  </div>)}
              </div>
            </section>

            {/* Top Sources */}
            <section className="bg-card border border-border rounded-lg p-6" data-id="element-767">
              <h3 className="text-base font-semibold mb-6" data-id="element-768">Top testate</h3>
              <div className="space-y-4" data-id="element-769">
                {topSources.map(source => <div key={source.name} className="flex items-center justify-between text-sm" data-id="element-770">
                    <span className="text-foreground font-medium truncate pr-4" data-id="element-771">
                      {source.name}
                    </span>
                    <div className="flex items-center gap-4 text-muted-foreground flex-shrink-0" data-id="element-772">
                      <span style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-773">
                        {source.articles} art.
                      </span>
                      <span className="text-foreground font-semibold w-8 text-right" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-774">
                        {source.avgScore.toFixed(1)}
                      </span>
                    </div>
                  </div>)}
              </div>
            </section>
          </div>

          {/* Spokespersons */}
          <section className="bg-card border border-border rounded-lg p-6" data-id="element-775">
            <h3 className="text-base font-semibold mb-6" data-id="element-776">Portavoce citati</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-777">
              {spokespersonMentions.map(person => <div key={person.name} className="flex items-center justify-between text-sm p-4 border border-border rounded-md bg-muted/10" data-id="element-778">
                  <div className="min-w-0 pr-4" data-id="element-779">
                    <div className="text-foreground font-medium truncate" data-id="element-780">
                      {person.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5" data-id="element-781">
                      {person.role}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0" data-id="element-782">
                    <span className="font-semibold text-lg" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-783">
                      {person.mentions}
                    </span>
                    <div className={`w-2 h-2 rounded-full ${getSentimentColor(person.avgSentiment)}`} data-id="element-784" />
                  </div>
                </div>)}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="articles" className="m-0 p-6 max-w-[1400px] mx-auto" data-id="element-785">
          <div className="bg-card border border-border rounded-lg overflow-hidden" data-id="element-786">
            {/* Mobile */}
            <div className="md:hidden divide-y divide-border" data-id="element-787">
              {rassegnaArticles.map(article => <button key={article.id} onClick={() => navigate(`/article/${article.id}`)} className="w-full text-left py-5 hover:bg-muted/30 px-4 transition-colors" data-id="element-788">
                  <div className="flex items-center gap-2 mb-1.5 text-sm text-muted-foreground" data-id="element-789">
                    <img src={article.sourceLogo} alt="" className="w-4 h-4 rounded-sm" data-id="element-790" />
                    <span data-id="element-791">{article.source}</span>
                    <span data-id="element-792">·</span>
                    <span data-id="element-793">
                      {new Date(article.date).toLocaleDateString('it-IT', {
                    day: '2-digit',
                    month: 'short'
                  })}
                    </span>
                  </div>
                  <h3 className="font-medium text-base leading-snug mb-2 line-clamp-2" data-id="element-794">
                    {article.title}
                  </h3>
                  <div className="flex items-center gap-4 text-sm" data-id="element-795">
                    <span className="font-semibold" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-796">
                      {article.relevanceScore.toFixed(1)}
                    </span>
                    <span className="text-muted-foreground" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-797">
                      €{(article.ave / 1000).toFixed(1)}k
                    </span>
                    <div className={`w-1.5 h-1.5 rounded-full ${getSentimentColor(article.sentiment)}`} data-id="element-798" />
                  </div>
                </button>)}
            </div>

            {/* Desktop */}
            <div className="hidden md:block" data-id="element-799">
              <Table data-id="element-800">
                <TableHeader data-id="element-801">
                  <TableRow className="hover:bg-transparent" data-id="element-802">
                    <TableHead className="w-[100px] text-sm" data-id="element-803">Data</TableHead>
                    <TableHead className="text-sm" data-id="element-804">Testata</TableHead>
                    <TableHead className="w-[90px] text-sm" data-id="element-805">Tier</TableHead>
                    <TableHead className="text-sm" data-id="element-806">Titolo</TableHead>
                    <TableHead className="w-[150px] text-sm" data-id="element-807">
                      Tipologia
                    </TableHead>
                    <TableHead className="text-right w-[80px] text-sm" data-id="element-808">
                      Score
                    </TableHead>
                    <TableHead className="text-right w-[80px] text-sm" data-id="element-809">
                      AVE
                    </TableHead>
                    <TableHead className="w-[60px]" data-id="element-810"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-id="element-811">
                  {rassegnaArticles.map(article => <TableRow key={article.id} className="cursor-pointer" onClick={() => navigate(`/article/${article.id}`)} data-id="element-812">
                      <TableCell className="text-[15px] text-muted-foreground whitespace-nowrap py-4" data-id="element-813">
                        {new Date(article.date).toLocaleDateString('it-IT')}
                      </TableCell>
                      <TableCell className="py-4" data-id="element-814">
                        <div className="flex items-center gap-2" data-id="element-815">
                          <img src={article.sourceLogo} alt="" className="w-5 h-5 rounded-sm" data-id="element-816" />
                          <span className="text-[15px]" data-id="element-817">{article.source}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4" data-id="element-818">
                        <Badge className={`text-[10px] px-1.5 py-0 border-0 ${getTierColor(article.tier)}`} data-id="element-819">
                          {tierLabels[article.tier]}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[400px] py-4" data-id="element-820">
                        <div className="text-[15px] font-medium truncate" data-id="element-821">
                          {article.title}
                        </div>
                      </TableCell>
                      <TableCell className="text-[15px] text-muted-foreground py-4" data-id="element-822">
                        {contentTypeLabels[article.contentType]}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-[15px] py-4" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-823">
                        {article.relevanceScore.toFixed(1)}
                      </TableCell>
                      <TableCell className="text-right text-[15px] text-muted-foreground py-4" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-824">
                        €{(article.ave / 1000).toFixed(1)}k
                      </TableCell>
                      <TableCell className="py-4" data-id="element-825">
                        <div className={`w-2 h-2 rounded-full ${getSentimentColor(article.sentiment)} mx-auto`} data-id="element-826" />
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
      </div>
    </Tabs>;
}